import { DateRange } from '@/components/DateRangeSelector'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getLateEmployees } from '@/modules/Dashboard/services'
import { LateEmployee } from '@/modules/Dashboard/types'
import { AlertTriangle, Clock, TrendingDown, Trophy, User } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { getWorkdaysReport } from '../../../services'
import { WorkdayReportItem } from '../../../types/response'

type ViewMode = 'late_minutes' | 'late_count' | 'best_attendance'

interface TopEmployeesWidgetProps {
    dateRange: DateRange
}

interface AggregatedEmployee {
    employee_id: number
    employee_name: string
    employee_code: string
    department_name: string
    total_late_minutes: number
    late_count: number
}

// Calculate late minutes based on work_day_option
const calculateLateMinutes = (employee: LateEmployee): number => {
    const [hours, minutes] = employee.work_day_in.split(':').map(Number)
    const checkInMinutes = hours * 60 + minutes
    const isMorningShift =
        employee.work_day_option === 0 ||
        employee.work_day_option === 2 ||
        employee.work_day_option === null
    const lateThreshold = isMorningShift ? 8 * 60 + 30 : 13 * 60
    return Math.max(0, checkInMinutes - lateThreshold)
}

const TopEmployeesSkeleton = () => (
    <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
            <div
                key={i}
                className="flex items-center gap-2 p-2 border rounded-lg"
            >
                <Skeleton className="w-6 h-6 rounded-full" />
                <div className="flex-1">
                    <Skeleton className="h-3 w-24 mb-1" />
                    <Skeleton className="h-2 w-16" />
                </div>
                <Skeleton className="h-4 w-12" />
            </div>
        ))}
    </div>
)

const MEDAL_COLORS = [
    'bg-yellow-100 text-yellow-700 border-yellow-300', // Gold
    'bg-gray-100 text-gray-600 border-gray-300', // Silver
    'bg-amber-100 text-amber-700 border-amber-300', // Bronze
    'bg-slate-50 text-slate-600 border-slate-200', // 4th
    'bg-slate-50 text-slate-500 border-slate-200', // 5th
]

const MEDAL_ICONS = ['1', '2', '3', '4', '5']

const TopEmployeesWidget: React.FC<TopEmployeesWidgetProps> = ({
    dateRange,
}) => {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<ViewMode>('late_minutes')

    // Fetch late employees data
    const { data: lateData, isLoading: isLoadingLate } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.LATE_EMPLOYEES,
            'aggregated',
            dateRange.gte,
            dateRange.lte,
        ],
        queryFn: () => getLateEmployees(dateRange),
        staleTime: 5 * 60 * 1000,
    })

    // Fetch workdays report for best attendance
    const { data: reportData, isLoading: isLoadingReport } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.WORKDAYS_REPORT,
                'top',
                dateRange.gte,
                dateRange.lte,
            ],
            queryFn: () => getWorkdaysReport(dateRange),
            staleTime: 5 * 60 * 1000,
        })

    // Aggregate late data by employee
    const aggregatedLateData = useMemo(() => {
        if (!lateData || !Array.isArray(lateData)) return []

        const employeeMap = new Map<number, AggregatedEmployee>()

        lateData.forEach((emp: LateEmployee) => {
            const lateMinutes = calculateLateMinutes(emp)
            const existing = employeeMap.get(emp.employee_id)

            if (existing) {
                existing.total_late_minutes += lateMinutes
                existing.late_count += 1
            } else {
                employeeMap.set(emp.employee_id, {
                    employee_id: emp.employee_id,
                    employee_name: emp.employee_name,
                    employee_code: emp.employee_code,
                    department_name: emp.department_name,
                    total_late_minutes: lateMinutes,
                    late_count: 1,
                })
            }
        })

        return Array.from(employeeMap.values())
    }, [lateData])

    // Top 5 by late minutes
    const topByLateMinutes = useMemo(() => {
        return [...aggregatedLateData]
            .sort((a, b) => b.total_late_minutes - a.total_late_minutes)
            .slice(0, 5)
    }, [aggregatedLateData])

    // Top 5 by late count
    const topByLateCount = useMemo(() => {
        return [...aggregatedLateData]
            .sort((a, b) => b.late_count - a.late_count)
            .slice(0, 5)
    }, [aggregatedLateData])

    // Top 5 best attendance (no late, highest attendance_days, must have work days)
    const topBestAttendance = useMemo(() => {
        if (!reportData || !Array.isArray(reportData)) return []

        // Get employee IDs who have late records
        const lateEmployeeIds = new Set(
            aggregatedLateData.map((e) => e.employee_id)
        )

        // Filter employees with no late, has work days > 0, sort by attendance_days desc
        return [...reportData]
            .filter(
                (emp: WorkdayReportItem) =>
                    !lateEmployeeIds.has(emp.employee_id) &&
                    emp.total_work_days > 0
            )
            .sort((a, b) => b.attendance_days - a.attendance_days)
            .slice(0, 5)
    }, [reportData, aggregatedLateData])

    const isLoading = isLoadingLate || isLoadingReport

    const getCurrentData = () => {
        switch (viewMode) {
            case 'late_minutes':
                return topByLateMinutes
            case 'late_count':
                return topByLateCount
            case 'best_attendance':
                return topBestAttendance
            default:
                return []
        }
    }

    const getViewConfig = () => {
        switch (viewMode) {
            case 'late_minutes':
                return {
                    icon: <Clock className="w-4 h-4" />,
                    title: t('workday.top_late_minutes'),
                    emptyText: t('workday.no_late_data'),
                    color: 'text-red-500',
                    bgColor: 'bg-red-50',
                }
            case 'late_count':
                return {
                    icon: <AlertTriangle className="w-4 h-4" />,
                    title: t('workday.top_late_count'),
                    emptyText: t('workday.no_late_data'),
                    color: 'text-orange-500',
                    bgColor: 'bg-orange-50',
                }
            case 'best_attendance':
                return {
                    icon: <Trophy className="w-4 h-4" />,
                    title: t('workday.top_best_attendance'),
                    emptyText: t('workday.no_attendance_data'),
                    color: 'text-green-500',
                    bgColor: 'bg-green-50',
                }
        }
    }

    const config = getViewConfig()
    const currentData = getCurrentData()

    const renderValue = (item: AggregatedEmployee | WorkdayReportItem) => {
        if (viewMode === 'late_minutes') {
            const emp = item as AggregatedEmployee
            return (
                <div className="text-right">
                    <div className="text-xs font-bold text-red-600">
                        +{emp.total_late_minutes}p
                    </div>
                    <div className="text-[9px] text-gray-400">
                        {emp.late_count} {t('workday.times')}
                    </div>
                </div>
            )
        }
        if (viewMode === 'late_count') {
            const emp = item as AggregatedEmployee
            return (
                <div className="text-right">
                    <div className="text-xs font-bold text-orange-600">
                        {emp.late_count} {t('workday.times')}
                    </div>
                    <div className="text-[9px] text-gray-400">
                        +{emp.total_late_minutes}p
                    </div>
                </div>
            )
        }
        if (viewMode === 'best_attendance') {
            const emp = item as WorkdayReportItem
            return (
                <div className="text-right">
                    <div className="text-xs font-bold text-green-600">
                        {emp.attendance_days} {t('workday.days_short')}
                    </div>
                    <div className="text-[9px] text-gray-400">
                        {emp.total_work_days} {t('workday.total_short')}
                    </div>
                </div>
            )
        }
    }

    return (
        <div className="bg-white rounded-lg p-3 h-full flex flex-col">
            {/* Header with tabs */}
            <div className="flex items-center justify-between mb-3">
                <div className={`flex items-center gap-1.5 ${config.color}`}>
                    {config.icon}
                    <Typogaphy className="font-semibold text-sm">
                        Top 5
                    </Typogaphy>
                </div>
            </div>

            {/* View Mode Tabs */}
            <div className="flex gap-1 mb-3">
                <button
                    onClick={() => setViewMode('late_minutes')}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
                        viewMode === 'late_minutes'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                >
                    <TrendingDown className="w-3 h-3" />
                    {t('workday.late_minutes_tab')}
                </button>
                <button
                    onClick={() => setViewMode('late_count')}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
                        viewMode === 'late_count'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                >
                    <AlertTriangle className="w-3 h-3" />
                    {t('workday.late_count_tab')}
                </button>
                <button
                    onClick={() => setViewMode('best_attendance')}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
                        viewMode === 'best_attendance'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}
                >
                    <Trophy className="w-3 h-3" />
                    {t('workday.best_attendance_tab')}
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                    <TopEmployeesSkeleton />
                ) : currentData.length > 0 ? (
                    <div className="space-y-1.5">
                        {currentData.map((item, index) => {
                            const emp = item as
                                | AggregatedEmployee
                                | WorkdayReportItem
                            const name =
                                'employee_name' in emp ? emp.employee_name : ''
                            const dept =
                                'department_name' in emp
                                    ? emp.department_name
                                    : ''

                            return (
                                <div
                                    key={
                                        'employee_id' in emp
                                            ? emp.employee_id
                                            : index
                                    }
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all hover:shadow-sm ${MEDAL_COLORS[index]}`}
                                >
                                    {/* Rank */}
                                    <div
                                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                            index === 0
                                                ? 'bg-yellow-400 text-yellow-900'
                                                : index === 1
                                                  ? 'bg-gray-300 text-gray-700'
                                                  : index === 2
                                                    ? 'bg-amber-400 text-amber-900'
                                                    : 'bg-slate-200 text-slate-600'
                                        }`}
                                    >
                                        {MEDAL_ICONS[index]}
                                    </div>

                                    {/* Employee Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1">
                                            <User className="w-3 h-3 opacity-50" />
                                            <Typogaphy className="text-xs font-medium truncate capitalize">
                                                {name}
                                            </Typogaphy>
                                        </div>
                                        <Typogaphy className="text-[9px] text-gray-500 truncate">
                                            {dept}
                                        </Typogaphy>
                                    </div>

                                    {/* Value */}
                                    {renderValue(item)}
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="text-center py-6">
                        <div
                            className={`w-10 h-10 mx-auto mb-2 ${config.bgColor} rounded-full flex items-center justify-center`}
                        >
                            {viewMode === 'best_attendance' ? (
                                <Trophy className={`w-5 h-5 ${config.color}`} />
                            ) : (
                                <Clock className={`w-5 h-5 ${config.color}`} />
                            )}
                        </div>
                        <Typogaphy className="text-gray-500 text-xs">
                            {config.emptyText}
                        </Typogaphy>
                    </div>
                )}
            </div>
        </div>
    )
}

export default TopEmployeesWidget
